import { Link } from 'react-router-dom'
import { useActivity, useJobs, useLocales, usePlatforms } from '../api/hooks'
import { ProgressRow } from '../components/Progress'
import { labelMap } from '../lib/locales'
import { timeAgo } from '../lib/time'
import type { ActivityEntry } from '../api/types'

const ACTOR_GLYPH: Record<ActivityEntry['actor_type'], string> = {
  user: '✎',
  ai: '◆',
  api: '⇅',
  import: '⇩',
  revert: '↺',
}

export function Dashboard() {
  const platforms = usePlatforms()
  const locales = useLocales()
  const jobs = useJobs()
  const activity = useActivity()
  const labels = labelMap(locales.data ?? [])
  const pending = (jobs.data?.counts.pending ?? 0) + (jobs.data?.counts.running ?? 0)
  const failed = jobs.data?.counts.failed ?? 0

  return (
    <div className="h-full overflow-y-auto bg-ink-50 px-4 py-4">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-base font-semibold text-ink-900">Platforms</h1>
            <p className="text-xs text-ink-600">
              English is the source. German ships in two variants —{' '}
              <span className="font-medium text-ink-700">Sie (formal)</span> and{' '}
              <span className="font-medium text-ink-700">du (informal)</span> — tracked separately.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="chip border-violet-300 bg-violet-50 text-violet-800">
              <span aria-hidden="true">◆</span> {pending} AI job{pending === 1 ? '' : 's'} pending
            </span>
            {failed > 0 && (
              <span className="chip border-rose-300 bg-rose-50 text-rose-900">
                <span aria-hidden="true">✕</span> {failed} failed
              </span>
            )}
          </div>
        </div>

        {platforms.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
            Could not load platforms: {(platforms.error as Error).message}
          </p>
        )}

        {(platforms.isLoading || locales.isLoading) && (
          <p className="text-xs text-ink-600">Loading platforms…</p>
        )}

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {/* Locale metadata carries the Sie/Du labelling, so cards wait for it
              rather than briefly rendering unlabelled progress. */}
          {(locales.data ? (platforms.data ?? []) : []).map((platform) => (
            <section
              key={platform.id}
              className="flex flex-col rounded-lg border border-ink-200 bg-white p-3 shadow-sm"
            >
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-ink-900">{platform.name}</h2>
                <span className="font-mono text-2xs text-ink-600">{platform.default_format}</span>
              </div>
              <p className="mt-0.5 font-mono text-2xs text-ink-600">{platform.slug}</p>
              <p className="mt-2 text-xs text-ink-600">
                <span className="text-base font-semibold tabular-nums text-ink-900">
                  {platform.total_keys}
                </span>{' '}
                keys
              </p>
              <div className="mt-3 flex-1 space-y-2.5">
                {Object.entries(platform.progress).map(([code, value]) => {
                  const label = labels[code]
                  return label ? <ProgressRow key={code} label={label} value={value} /> : null
                })}
              </div>
              <Link
                to={`/p/${platform.slug}/default`}
                className="btn btn-primary mt-3 justify-center"
              >
                Open editor
              </Link>
            </section>
          ))}
        </div>

        <section className="rounded-lg border border-ink-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-ink-200 px-3 py-2">
            <h2 className="text-sm font-semibold text-ink-900">Recent activity</h2>
            <Link to="/activity" className="text-2xs text-blue-700 underline hover:no-underline">
              View all
            </Link>
          </div>
          {activity.isError && (
            <p role="alert" className="px-3 py-2 text-xs text-rose-900">
              Could not load activity: {(activity.error as Error).message}
            </p>
          )}
          {activity.isLoading && <p className="px-3 py-4 text-xs text-ink-600">Loading activity…</p>}
          {activity.data && (
            <ul className="divide-y divide-ink-100">
              {activity.data.entries.map((entry) => (
                <li key={entry.id} className="flex items-baseline gap-2 px-3 py-1.5 text-xs">
                  <span
                    className="w-4 shrink-0 text-center text-ink-600"
                    title={entry.actor_type}
                    aria-label={entry.actor_type}
                  >
                    {ACTOR_GLYPH[entry.actor_type]}
                  </span>
                  <span
                    className="shrink-0 text-2xs text-ink-700"
                    title={entry.actor.email ?? undefined}
                  >
                    {entry.actor.display}
                  </span>
                  <span className="font-mono text-2xs text-ink-700">{entry.key_name}</span>
                  <span className="chip border-ink-200 bg-ink-50 text-ink-600">{entry.locale}</span>
                  <span className="min-w-0 flex-1 truncate text-ink-600">
                    {entry.old_value ? (
                      <>
                        <span className="line-through text-ink-600">{entry.old_value}</span>{' '}
                        <span aria-hidden="true">→</span>{' '}
                      </>
                    ) : null}
                    <span className="text-ink-800">{entry.new_value}</span>
                  </span>
                  <span className="shrink-0 text-2xs text-ink-600">{timeAgo(entry.created_at)}</span>
                </li>
              ))}
              {activity.data.entries.length === 0 && (
                <li className="px-3 py-4 text-xs text-ink-600">No changes recorded yet.</li>
              )}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
